import React, { useCallback, useEffect, useState } from "react";
import { TAVOLOZZA, tintaDaValore } from "@/lib/committenti";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { fileUrl } from "@/lib/placeholders";
import {
  Building2, Plus, Trash2, Pencil, Check, X, Merge, ImagePlus,
} from "lucide-react";

/**
 * Anagrafica committenti e unificazione delle diciture scritte a mano.
 *
 * Le diciture non vengono accorpate per somiglianza: "AP reti gas north" e
 * "AP reti gas nord ovest" si assomigliano ma sono due clienti diversi, e la
 * differenza la sa solo chi lavora con loro. Qui si mostra cosa c'e' scritto
 * sui cantieri e si lascia decidere.
 */
/**
 * Il logo del committente, che finisce in testata al frontespizio del POS.
 * Si carica cliccandolo: e' il posto in cui uno lo cerca.
 */
function LogoCommittente({ committente, onCaricato }) {
  const [busy, setBusy] = useState(false);
  const campo = React.useRef(null);

  const scegli = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/pos/committenti/${committente.id}/logo`, fd,
                     { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Logo di ${committente.name} caricato`);
      onCaricato?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento del logo");
    } finally { setBusy(false); }
  };

  return (
    <>
      <input ref={campo} type="file" accept=".png,.jpg,.jpeg" className="hidden"
             onChange={scegli} />
      <button type="button" onClick={() => campo.current?.click()} disabled={busy}
              title={committente.logo_url ? "Sostituisci il logo" : "Carica il logo"}
              data-testid={`logo-${committente.id}`}
              className="w-14 h-9 flex-shrink-0 border border-slate-200 rounded bg-white
                         flex items-center justify-center overflow-hidden
                         hover:border-[#0067b2] transition-colors">
        {committente.logo_url
          ? <img src={fileUrl(committente.logo_url)} alt=""
                 className="max-w-full max-h-full object-contain" />
          : <ImagePlus className="w-3.5 h-3.5 text-slate-300" />}
      </button>
    </>
  );
}

// Il pallino del colore: toccandolo si apre la tavolozza e si sceglie. Il
// colore automatico resta come ripiego, e si torna a quello con «automatico».
function ColoreCommittente({ committente, onCambiato }) {
  const [aperto, setAperto] = useState(false);
  const [busy, setBusy] = useState(false);
  const attuale = tintaDaValore(committente.tinta || committente.colore);

  const scegli = async (hex) => {
    setBusy(true);
    try {
      await api.put(`/committenti/${committente.id}/colore`, { tinta: hex });
      setAperto(false);
      onCambiato();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <div className="relative flex-shrink-0">
      <button type="button" title="Colore in pianificazione e nei cantieri: tocca per cambiarlo"
              onClick={() => setAperto(v => !v)} data-testid={`colore-${committente.id}`}
              className="w-6 h-6 rounded-full border-2 border-white shadow ring-1 ring-slate-300"
              style={{ backgroundColor: attuale ? `rgb(${attuale})` : "#cbd5e1" }} />
      {aperto && (
        <div className="absolute z-20 top-8 left-0 bg-white border border-slate-200 rounded-lg shadow-lg p-2 w-[232px]"
             onMouseLeave={() => setAperto(false)}>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Scegli il colore</div>
          <div className="grid grid-cols-8 gap-1">
            {TAVOLOZZA.map(([hex, nome]) => (
              <button key={hex} type="button" title={nome} disabled={busy}
                      onClick={() => scegli(hex)}
                      className={`w-6 h-6 rounded-full border-2 ${committente.tinta === hex ? "border-slate-900" : "border-white"} ring-1 ring-slate-200`}
                      style={{ backgroundColor: hex }} />
            ))}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <label className="text-xs text-slate-600 flex items-center gap-1 cursor-pointer">
              altro
              <input type="color" className="w-6 h-6 p-0 border-0 bg-transparent cursor-pointer"
                     defaultValue={committente.tinta || "#0067b2"}
                     onChange={(e) => scegli(e.target.value)} />
            </label>
            {committente.tinta && (
              <button type="button" className="text-xs text-slate-500 hover:text-slate-800 ml-auto"
                      onClick={() => scegli(null)}>automatico</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function GestioneCommittenti({ onDone }) {
  const [aperto, setAperto] = useState(false);
  const [committenti, setCommittenti] = useState([]);
  const [diciture, setDiciture] = useState([]);
  const [senzaCommittente, setSenzaCommittente] = useState(0);
  const [nuovo, setNuovo] = useState("");
  const [inModifica, setInModifica] = useState(null);
  const [nomeModificato, setNomeModificato] = useState("");
  const [scelte, setScelte] = useState([]);
  const [destinazione, setDestinazione] = useState("");
  const [busy, setBusy] = useState(false);
  const [modelli, setModelli] = useState([]);

  const carica = useCallback(async () => {
    try {
      const [a, b, m] = await Promise.all([
        api.get("/committenti"),
        api.get("/committenti/diciture"),
        api.get("/pos/modelli"),
      ]);
      setCommittenti(a.data);
      setDiciture(b.data.diciture);
      setSenzaCommittente(b.data.cantieri_senza_committente);
      setModelli(m.data);
    } catch { toast.error("Errore nel caricamento dei committenti"); }
  }, []);

  // Quale modello di POS usa il committente: da qui il frontespizio di
  // cantiere sa gia' come deve venire, senza chiederlo al tecnico ogni volta.
  const cambiaModello = async (id, modello) => {
    try {
      await api.put(`/pos/committenti/${id}`, { modello_pos: modello });
      setCommittenti((elenco) => elenco.map(
        (c) => (c.id === id ? { ...c, modello_pos: modello } : c)));
      toast.success("Modello di POS aggiornato");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  useEffect(() => { if (aperto) carica(); }, [aperto, carica]);

  const chiudi = () => {
    setAperto(false);
    setScelte([]); setDestinazione(""); setNuovo(""); setInModifica(null);
    onDone?.();
  };

  const aggiungi = async () => {
    const nome = nuovo.trim();
    if (!nome) return;
    setBusy(true);
    try {
      await api.post("/committenti", { name: nome });
      toast.success(`"${nome}" aggiunto`);
      setNuovo("");
      carica();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const rinomina = async (id) => {
    const nome = nomeModificato.trim();
    if (!nome) return;
    try {
      const { data } = await api.put(`/committenti/${id}`, { name: nome });
      toast.success(data.cantieri_aggiornati
        ? `Rinominato, ${data.cantieri_aggiornati} cantieri aggiornati`
        : "Rinominato");
      setInModifica(null);
      carica();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const elimina = async (c) => {
    let url = `/committenti/${c.id}`;
    if (c.cantieri > 0) {
      const altri = committenti.filter(x => x.id !== c.id);
      if (altri.length === 0) { toast.error("Non c'è un altro committente su cui spostarli"); return; }
      const nome = window.prompt(
        `"${c.name}" ha ${c.cantieri} cantieri collegati.\n\n` +
        `Scrivi il nome esatto del committente su cui spostarli:\n\n` +
        altri.map(x => `· ${x.name}`).join("\n"));
      if (!nome) return;
      const scelto = altri.find(x => x.name.toLowerCase() === nome.trim().toLowerCase());
      if (!scelto) { toast.error("Nome non riconosciuto, niente è stato toccato"); return; }
      url += `?sposta_a=${scelto.id}`;
    } else if (!window.confirm(`Eliminare "${c.name}"?`)) {
      return;
    }
    try {
      const { data } = await api.delete(url);
      toast.success(`"${data.eliminato}" eliminato`
        + (data.cantieri_spostati ? `, ${data.cantieri_spostati} cantieri spostati` : ""));
      carica();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const assegna = async () => {
    if (!destinazione) { toast.error("Scegli a quale committente assegnarle"); return; }
    if (scelte.length === 0) { toast.error("Non hai spuntato nessuna dicitura"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/committenti/assegna",
        { diciture: scelte, committente_id: destinazione });
      toast.success(`${data.cantieri_aggiornati} cantieri assegnati a ${data.committente}`);
      setScelte([]); setDestinazione("");
      carica();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const spunta = (d) =>
    setScelte(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);

  return (<>
    <Button variant="outline" className="h-11" onClick={() => setAperto(true)}
            data-testid="committenti-btn">
      <Building2 className="w-4 h-4 mr-2" /> Committenti
    </Button>

    {aperto && (
      <Dialog open onOpenChange={chiudi}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Committenti</DialogTitle></DialogHeader>

          <div className="space-y-5">
            {/* --- diciture da sistemare ------------------------------- */}
            {diciture.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded space-y-3">
                <div>
                  <div className="text-sm font-semibold flex items-center gap-2">
                    <Merge className="w-4 h-4" />
                    {diciture.length} diciture scritte a mano da sistemare
                  </div>
                  <p className="text-xs text-amber-800 mt-1">
                    Spunta quelle che sono lo stesso cliente e assegnale. Occhio ai nomi
                    simili: possono essere clienti diversi.
                  </p>
                </div>

                <div className="border border-amber-200 rounded bg-white divide-y divide-slate-100 max-h-56 overflow-y-auto">
                  {diciture.map(d => (
                    <label key={d.dicitura}
                           className="p-2 flex items-center gap-3 cursor-pointer hover:bg-slate-50">
                      <input type="checkbox" checked={scelte.includes(d.dicitura)}
                             onChange={() => spunta(d.dicitura)} />
                      <span className="font-semibold text-sm flex-1 truncate">{d.dicitura}</span>
                      <span className="text-xs text-slate-500 flex-shrink-0">
                        {d.cantieri} cantier{d.cantieri === 1 ? "e" : "i"}
                      </span>
                    </label>
                  ))}
                </div>

                <div className="flex gap-2 flex-wrap items-end">
                  <div className="flex-1 min-w-[180px]">
                    <Label className="text-xs">Assegna a</Label>
                    <select className="w-full h-10 border border-slate-200 rounded px-3 mt-1 bg-white"
                            value={destinazione} onChange={(e) => setDestinazione(e.target.value)}>
                      <option value="">Scegli committente...</option>
                      {committenti.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <Button onClick={assegna} disabled={busy || scelte.length === 0 || !destinazione}
                          className="bg-orange-500 hover:bg-orange-600 text-white">
                    Assegna {scelte.length > 0 ? scelte.length : ""}
                  </Button>
                </div>
              </div>
            )}

            {senzaCommittente > 0 && (
              <p className="text-xs text-slate-500">
                {senzaCommittente} cantier{senzaCommittente === 1 ? "e" : "i"} non
                {senzaCommittente === 1 ? " ha" : " hanno"} nessun committente indicato.
              </p>
            )}

            {/* --- anagrafica ------------------------------------------ */}
            <div className="space-y-2">
              <div className="text-sm font-semibold">Elenco committenti</div>
              <div className="border border-slate-200 rounded divide-y divide-slate-100">
                {committenti.map(c => (
                  <div key={c.id} className="p-2 flex items-center gap-2">
                    {inModifica === c.id ? (<>
                      <Input className="h-8 flex-1" value={nomeModificato}
                             onChange={(e) => setNomeModificato(e.target.value)}
                             onKeyDown={(e) => { if (e.key === "Enter") rinomina(c.id); }} />
                      <Button size="sm" variant="outline" onClick={() => rinomina(c.id)}>
                        <Check className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setInModifica(null)}>
                        <X className="w-3 h-3" />
                      </Button>
                    </>) : (<>
                      <LogoCommittente committente={c} onCaricato={carica} />
                      <ColoreCommittente committente={c} onCambiato={carica} />
                      <span className="flex-1 text-sm font-medium truncate">{c.name}</span>
                      <select className="h-8 border border-slate-200 rounded px-2 text-xs bg-white max-w-[168px]"
                              value={c.modello_pos || "sgm"} title="Modello di POS usato da questo committente"
                              onChange={(e) => cambiaModello(c.id, e.target.value)}
                              data-testid={`modello-pos-${c.id}`}>
                        {modelli.map(m => <option key={m.codice} value={m.codice}>{m.nome}</option>)}
                      </select>
                      <Badge variant="outline" className="text-[10px] flex-shrink-0">
                        {c.cantieri} cantieri
                      </Badge>
                      <button type="button" title="Rinomina" className="text-slate-400 hover:text-slate-700 px-1"
                              onClick={() => { setInModifica(c.id); setNomeModificato(c.name); }}>
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button type="button" title="Elimina" className="text-red-500 hover:text-red-700 px-1"
                              onClick={() => elimina(c)}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </>)}
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <Input placeholder="Nuovo committente" value={nuovo}
                       onChange={(e) => setNuovo(e.target.value)}
                       onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); aggiungi(); } }} />
                <Button variant="outline" onClick={aggiungi} disabled={busy || !nuovo.trim()}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <DialogFooter>
              <Button onClick={chiudi} className="bg-orange-500 hover:bg-orange-600 text-white">
                Chiudi
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    )}
  </>);
}
